import { useState } from "react";
import { Container, Paper, Title, Text, Group, Stack, Badge, Button, Card, Grid, Textarea, NumberInput, Checkbox, Divider } from "@mantine/core";
import { IconArrowLeft, IconDownload, IconCheck, IconX, IconClipboardList } from "@tabler/icons-react";
import { useNavigate, useParams } from "react-router-dom";
import { useTechnicalEvaluationById, useUpdateTechnicalEvaluation, useUpdateDocumentReview } from "../hooks/useTechnicalEvaluation";
import { useTenderById } from "../hooks/useTenders";
import { formatCurrency } from '../../../utils/currency';
import { notifications } from "@mantine/notifications";
import dayjs from "dayjs";

export function TechnicalEvaluationDetail() {
  const navigate = useNavigate();
  const { tenderId, evaluationId } = useParams<{ tenderId: string; evaluationId: string }>();
  const { data: evaluation, isLoading, error, refetch } = useTechnicalEvaluationById(tenderId!, evaluationId!);
  const { data: tender } = useTenderById(tenderId!);
  const updateEvaluation = useUpdateTechnicalEvaluation();
  const updateDocumentReview = useUpdateDocumentReview();

  const [evaluationData, setEvaluationData] = useState({
    status: '',
    evaluationNotes: '',
    torComplianceScore: 0,
    documentComplianceScore: 0,
    isApproved: undefined as boolean | undefined,
    rejectionReason: '',
  });

  if (isLoading) {
    return (
      <Container size="xl" py="xl">
        <Text>Loading technical evaluation...</Text>
      </Container>
    );
  }

  if (error || !evaluation) {
    return (
      <Container size="xl" py="xl">
        <Text color="red">Failed to load technical evaluation</Text>
      </Container>
    );
  }

  const handleUpdateEvaluation = async () => {
    try {
      await updateEvaluation.mutateAsync({
        tenderId: tenderId!,
        evaluationId: evaluationId!,
        data: evaluationData,
      });
      refetch();
    } catch (error) {
      console.error("Error updating evaluation:", error);
    }
  };

  const handleUpdateDocumentReview = async (reviewId: string, data: any) => {
    try {
      await updateDocumentReview.mutateAsync({
        tenderId: tenderId!,
        evaluationId: evaluationId!,
        reviewId,
        data,
      });
      refetch();
    } catch (error) {
      console.error("Error updating document review:", error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'APPROVED': return 'green';
      case 'REJECTED': return 'red';
      case 'COMPLETED': return 'blue';
      case 'IN_PROGRESS': return 'yellow';
      default: return 'gray';
    }
  };

  return (
    <Container size="xl" py="xl">
      <Paper shadow="sm" p="xl">
        <Group mb="xl">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate(`/tenders/${tenderId}`)}
          >
            Back to Tender
          </Button>
        </Group>

        <Group justify="space-between" mb="xl">
          <div>
            <Title order={2}>Technical Evaluation</Title>
            <Text c="dimmed" size="sm">
              {evaluation.bidSubmission?.companyName} • {dayjs(evaluation.createdAt).format("MMM DD, YYYY")}
            </Text>
          </div>
          <Badge size="lg" color={getStatusColor(evaluation.status)}>
            {evaluation.status}
          </Badge>
        </Group>

        <Grid>
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Stack gap="xl">
              {/* Bid Submission Overview */}
              <Card shadow="sm" p="lg">
                <Title order={3} mb="md">
                  Bid Submission Overview
                </Title>
                <Grid>
                  <Grid.Col span={6}>
                    <Stack gap="sm">
                      <div>
                        <Text size="sm" c="dimmed">Company Name</Text>
                        <Text fw={500}>{evaluation.bidSubmission?.companyName}</Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">Total Price</Text>
                        <Text fw={500}>{formatCurrency(Number(evaluation.bidSubmission?.totalPrice || 0), tender?.currency || 'MVR')}</Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">Submitted At</Text>
                        <Text>{dayjs(evaluation.bidSubmission?.submittedAt).format("MMM DD, YYYY HH:mm")}</Text>
                      </div>
                    </Stack>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Stack gap="sm">
                      <div>
                        <Text size="sm" c="dimmed">Evaluator</Text>
                        <Text>{evaluation.evaluator?.fullName || "Not assigned"}</Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">Evaluation Date</Text>
                        <Text>{evaluation.evaluationDate ? dayjs(evaluation.evaluationDate).format("MMM DD, YYYY HH:mm") : "Not completed"}</Text>
                      </div>
                      <div>
                        <Text size="sm" c="dimmed">Overall Score</Text>
                        <Text fw={500} color="blue">
                          {evaluation.overallScore !== undefined ? `${evaluation.overallScore}%` : "Not scored"}
                        </Text>
                      </div>
                    </Stack>
                  </Grid.Col>
                </Grid>
              </Card>

              {/* Document Reviews */}
              <Card shadow="sm" p="lg">
                <Title order={3} mb="md" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <IconClipboardList size={20} />
                  Document Reviews
                </Title>
                
                {evaluation.documentReviews && evaluation.documentReviews.length > 0 ? (
                  <Stack gap="md">
                    {evaluation.documentReviews.map((review) => (
                      <Card key={review.id} shadow="xs" p="md" withBorder>
                        <Group justify="space-between" mb="sm">
                          <div>
                            <Text fw={500}>{review.documentName}</Text>
                            <Text size="sm" c="dimmed">{review.documentType}</Text>
                          </div>
                          <Group gap="xs">
                            <Badge 
                              size="sm" 
                              color={review.isSubmitted ? 'green' : 'red'}
                            >
                              {review.isSubmitted ? 'Submitted' : 'Missing'}
                            </Badge>
                            {review.isCompliant !== undefined && (
                              <Badge 
                                size="sm" 
                                color={review.isCompliant ? 'green' : 'red'}
                              >
                                {review.isCompliant ? 'Compliant' : 'Non-Compliant'}
                              </Badge>
                            )}
                          </Group>
                        </Group>

                        {review.isSubmitted && (
                          <Stack gap="sm" mt="md">
                            <Group grow>
                              <Checkbox
                                label="Document is compliant"
                                checked={review.isCompliant || false}
                                onChange={(e) => handleUpdateDocumentReview(review.id, { isCompliant: e.target.checked })}
                              />
                              <NumberInput
                                label="Score (0-100)"
                                placeholder="Enter score"
                                value={review.score || 0}
                                onChange={(value) => handleUpdateDocumentReview(review.id, { score: value || 0 })}
                                min={0}
                                max={100}
                              />
                            </Group>
                            <Textarea
                              label="Review Notes"
                              placeholder="Enter review notes for this document"
                              value={review.reviewNotes || ''}
                              onChange={(e) => handleUpdateDocumentReview(review.id, { reviewNotes: e.target.value })}
                              rows={2}
                            />
                          </Stack>
                        )}
                      </Card>
                    ))}
                  </Stack>
                ) : (
                  <Text c="dimmed">No document reviews found</Text>
                )}
              </Card>

              {/* Evaluation Form */}
              <Card shadow="sm" p="lg">
                <Title order={3} mb="md">
                  Evaluation Form
                </Title>
                
                <Stack gap="md">
                  <Group grow>
                    <NumberInput
                      label="TOR Compliance Score (0-100)"
                      placeholder="Enter TOR compliance score"
                      value={evaluationData.torComplianceScore}
                      onChange={(value) => setEvaluationData(prev => ({ ...prev, torComplianceScore: Number(value) || 0 }))}
                      min={0}
                      max={100}
                    />
                    <NumberInput
                      label="Document Compliance Score (0-100)"
                      placeholder="Enter document compliance score"
                      value={evaluationData.documentComplianceScore}
                      onChange={(value) => setEvaluationData(prev => ({ ...prev, documentComplianceScore: Number(value) || 0 }))}
                      min={0}
                      max={100}
                    />
                  </Group>

                  <Textarea
                    label="Evaluation Notes"
                    placeholder="Enter detailed evaluation notes"
                    value={evaluationData.evaluationNotes}
                    onChange={(e) => setEvaluationData(prev => ({ ...prev, evaluationNotes: e.target.value }))}
                    rows={4}
                  />

                  <Group>
                    <Checkbox
                      label="Approve this bid submission"
                      checked={evaluationData.isApproved === true}
                      onChange={(e) => setEvaluationData(prev => ({ 
                        ...prev, 
                        isApproved: e.target.checked ? true : undefined,
                        rejectionReason: e.target.checked ? '' : prev.rejectionReason
                      }))}
                    />
                    <Checkbox
                      label="Reject this bid submission"
                      checked={evaluationData.isApproved === false}
                      onChange={(e) => setEvaluationData(prev => ({ 
                        ...prev, 
                        isApproved: e.target.checked ? false : undefined,
                        rejectionReason: e.target.checked ? prev.rejectionReason : ''
                      }))}
                    />
                  </Group>

                  {evaluationData.isApproved === false && (
                    <Textarea
                      label="Rejection Reason"
                      placeholder="Provide detailed reason for rejection"
                      value={evaluationData.rejectionReason}
                      onChange={(e) => setEvaluationData(prev => ({ ...prev, rejectionReason: e.target.value }))}
                      rows={3}
                    />
                  )}

                  <Group justify="flex-end">
                    <Button
                      onClick={handleUpdateEvaluation}
                      loading={updateEvaluation.isPending}
                      leftSection={<IconCheck size={16} />}
                    >
                      Update Evaluation
                    </Button>
                  </Group>
                </Stack>
              </Card>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 4 }}>
            <Stack gap="md">
              {/* Current Scores */}
              <Card shadow="sm" p="lg">
                <Title order={3} mb="md">
                  Current Scores
                </Title>
                <Stack gap="sm">
                  <div>
                    <Text size="sm" c="dimmed">TOR Compliance</Text>
                    <Text fw={500} size="lg">
                      {evaluation.torComplianceScore !== undefined ? `${evaluation.torComplianceScore}%` : "Not scored"}
                    </Text>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Document Compliance</Text>
                    <Text fw={500} size="lg">
                      {evaluation.documentComplianceScore !== undefined ? `${evaluation.documentComplianceScore}%` : "Not scored"}
                    </Text>
                  </div>
                  <Divider />
                  <div>
                    <Text size="sm" c="dimmed">Overall Score</Text>
                    <Text fw={500} size="xl" color="blue">
                      {evaluation.overallScore !== undefined ? `${evaluation.overallScore}%` : "Not calculated"}
                    </Text>
                  </div>
                </Stack>
              </Card>

              {/* Final Decision */}
              <Card shadow="sm" p="lg">
                <Title order={3} mb="md">
                  Final Decision
                </Title>
                <Stack gap="sm">
                  <div>
                    <Text size="sm" c="dimmed">Status</Text>
                    <Badge size="lg" color={getStatusColor(evaluation.status)}>
                      {evaluation.status}
                    </Badge>
                  </div>
                  <div>
                    <Text size="sm" c="dimmed">Decision</Text>
                    {evaluation.isApproved !== undefined ? (
                      <Badge 
                        size="lg" 
                        color={evaluation.isApproved ? 'green' : 'red'}
                        leftSection={evaluation.isApproved ? <IconCheck size={16} /> : <IconX size={16} />}
                      >
                        {evaluation.isApproved ? 'Approved' : 'Rejected'}
                      </Badge>
                    ) : (
                      <Text c="dimmed">Not decided</Text>
                    )}
                  </div>
                  {evaluation.rejectionReason && (
                    <div>
                      <Text size="sm" c="dimmed">Rejection Reason</Text>
                      <Text size="sm">{evaluation.rejectionReason}</Text>
                    </div>
                  )}
                </Stack>
              </Card>

              {/* TOR Document */}
              {evaluation.tender?.torDocument && (
                <Card shadow="sm" p="lg">
                  <Title order={3} mb="md">
                    TOR Document
                  </Title>
                  <Button
                    variant="light"
                    leftSection={<IconDownload size={16} />}
                    fullWidth
                    onClick={() => {
                      // TODO: Implement TOR document download
                      notifications.show({
                        title: "Info",
                        message: "TOR document download functionality will be implemented",
                        color: "blue",
                      });
                    }}
                  >
                    Download TOR
                  </Button>
                </Card>
              )}
            </Stack>
          </Grid.Col>
        </Grid>
      </Paper>
    </Container>
  );
}
